
import { Component, Vue } from 'vue-property-decorator';
import { CalendarEvent, CalendarTimestamp } from 'vuetify';
import Themeable from 'vuetify/lib/mixins/themeable';
import ProfileRepository from '@/services/Users-profile';
import EventsRepository from '@/services/Calendar/Events';
import EmployeeBirthdaysRepository from '@/services/Calendar/EmployeeBirthdays';
import { EventType } from "@/services/Calendar/Events/Interfaces";
import PageHeader from '@/components/layout/PageHeader.vue';

@Component({
  mixins: [
    Themeable,
  ],
  components: {
    PageHeader,
  },
  inject: [
    'profileRepository',
    'eventsRepository',
    'employeeBirthdaysRepository',
  ],
})
export default class Events extends Vue {
  private readonly profileRepository!: ProfileRepository;
  private readonly eventsRepository!: EventsRepository;
  private readonly employeeBirthdaysRepository!: EmployeeBirthdaysRepository;

  private value: string = '';
  private type: string = 'month';
  private types: string[] = ['month', 'week', 'day'];
  private allEvents: Map<string, CalendarEvent[]> = new Map();
  private events: CalendarEvent[] = [];
  private selectedEvent: CalendarEvent = {};
  private selectedElement: unknown = null;
  selectedOpen: boolean = false;
  private startDate: string = '';
  private endDate: string = '';
  private weekday: number[] = [1, 2, 3, 4, 5, 6, 0];
  private categories: string[] = ['holidays', 'custom', 'birthdays'];
  private selectedCategories: string[] = [];
  private loading: boolean = true;

  public viewDay(data: CalendarTimestamp): void {
    this.value = data.date;
    this.type = 'week';
  }

  public setToday(): void {
    this.value = '';
  }

  public async getEvents(data: { start: CalendarTimestamp, end: CalendarTimestamp }): Promise<void> {
    this.allEvents = new Map();
    let startYear: string, startMonth: string, startDate: string, endYear: string, endMonth: string, endDate: string;

    if (typeof data !== "undefined") {
      [startYear, startMonth, startDate] = data.start.date.split('-');
      [endYear, endMonth, endDate] = data.end.date.split('-');
      this.startDate = data.start.date;
      this.endDate = data.end.date;
    } else {
      [startYear, startMonth, startDate] = this.startDate.split('-');
      [endYear, endMonth, endDate] = this.endDate.split('-');
    }

    const start = new Date(Date.UTC(Number(startYear), Number(startMonth) - 1, Number(startDate)));
    const end = new Date(Date.UTC(Number(endYear), Number(endMonth) - 1, Number(endDate), 23, 59, 59));
    const events = await this.eventsRepository.getEventsFromRangeOfDates(start, end);
    const holidays = events.filter((event) => event.type === EventType.national);
    this.allEvents.set('holidays', holidays.map((event) => {
      const date = new Date(event.date);
      return {
        id: event.id,
        name: event.name,
        start: date,
        end: new Date(`${date.toISOString().substr(0, 10)}T23:59:59`),
        color: 'red',
        timed: false,
        details: event.description,
      };
    }));

    const customEvents = events.filter((event) => event.type === EventType.custom);
    this.allEvents.set('custom', customEvents.map((event) => {
      const date = new Date(event.date);
      return {
        id: event.id,
        name: event.name,
        start: date,
        end: new Date(`${date.toISOString().substr(0, 10)}T23:59:59`),
        color: 'green',
        timed: false,
        details: event.description,
        attendance: event.attendance,
      };
    }));

    const employeeBirthdays = await this.employeeBirthdaysRepository.getBirthdaysForMonth(Number(startMonth) - 1);
    const employeeIds: string[] = [];
    employeeBirthdays.forEach((employee) => {
      employeeIds.push(employee.userId);
    });
    const employees = await this.profileRepository.getBaseProfileDataByUsersIds(employeeIds);
    this.allEvents.set('birthdays', employeeBirthdays.map((event) => {
      const date = new Date();
      const user = employees.filter(employee => employee.userId === event.userId)[0];
      date.setMonth(Number(startMonth) - 1);
      date.setDate(event.day);
      return {
        name: (user.fullName ? user.fullName : `${user.firstName} ${user.lastName}`) + '\'s Birthday',
        start: date,
        end: new Date(`${date.toISOString().substr(0, 10)}T23:59:59`),
        color: 'blue',
        timed: false,
        details: 'Happy Birthday to ' + (user.fullName ? user.fullName : `${user.firstName} ${user.lastName}`),
      };
    }));

    this.filterEvents();
  }

  public filterEvents(): void {
    this.events = [];

    this.allEvents.forEach((events, key) => {
      if (this.selectedCategories.length === 0 || this.selectedCategories.includes(key)) {
        events.forEach((event) => {
          this.events.push(event);
        });
      }
    });
  }

  public showEvent(data: { nativeEvent: any, event: CalendarEvent }): void {
    const open = () => {
      this.selectedEvent = data.event;
      this.selectedElement = data.nativeEvent.target;
      requestAnimationFrame(() => requestAnimationFrame(() => this.selectedOpen = true));
    }

    if (this.selectedOpen) {
      this.selectedOpen = false;
      requestAnimationFrame(() => requestAnimationFrame(() => open()));
    } else {
      open();
    }

    data.nativeEvent.stopPropagation();
  }

  public getEventColor(event: any): string {
    return event.color
  }

  private singUpForEvent(eventId: number): void {
    this.$router.push({ name: 'calendar_event_singup', params: { eventId: String(eventId) } });
  }

  private showAttendants(eventId: number): void {
    this.$router.push({ name: 'calendar_event_attendant', params: { eventId: String(eventId) } });
  }
}
